import { loadModules } from "esri-loader";
import { mapState, mapActions, mapGetters } from "vuex";
import Vue from "vue";
import MetadataService from "../services/metadata.service";
import SaveService from "../services/save.service";
import BasemapMixin from "@/mixins/basemapMixin";
import {
  createGraphicsLayer,
  importProfileFile,
  getLayerByID,
  removeAllGraphicsExceptLocationGraphic,
  switchMapTo3d,
  switchMapTo2d,
  getSavedMapDefinition,
  getLayerGraphicsAsJSON,
  getDuplicateLayerProfile,
  getUniqueLayerID,
  addLayerToMap,
  getFirstValidExtentFromURLQuery,
  placePhotoLayerOnTopIfActive,
  mergeProfileOptions,
  cloneObject,
  createMapLayer,
  getHydratedGraphics,
  getGraphicslayerMapProfile,
} from "./layerLogic";

const mappingMixin = {
  mixins: [BasemapMixin],
  computed: {
    ...mapState([
      "theMap",
      "profiles",
      "extent",
      "currentOrientation",
      "selectedBasemap",
      "mapProfiles",
      "isConnectedToIntranet",
      "basemaps",
      "selectedTheme",
      "is3d",
      "currentlyOpenedProject",
      "userAccount",
      "layerDefinitionExpressions",
    ]),
    ...mapGetters([
      "getMapProfile",
      "getAddedMapProfileIndex",
      "getMapProfilesVisibleSublayerIndexes",
    ]),
  },
  methods: {
    ...mapActions([
      "setSelectedBasemap",
      "setLayersAreLoading",
      "enableSave",
      "disableSave",
      "setExtent",
      "setSnack",
      "setCurrentOrientation",
      "setCurrentlyOpenedProject",
      "setPhotoStreamIsActivated",
      "addLastVisitedMap",
      "setBuildingsLayerAdded",
      "setPhotoStreamIsActivated",
      "setLayerDefinitionExpression",
      "setLogoImagePath",
      "addMapProfileToState",
      "setSelectedTheme",
      "setProfileActualVisibleSublayers",
      "addLayerMetadata",
      "setMapProfileDate",
      "setShowInfoMenu",
      "removeMapProfileFromState",
      "setCurrentMapScale",
      "setProfileVisibility",
      "setMapProfileOpacity",
      "addLayerInfo",
      "setStartupDialog",
      "setMapHeight",
      "setBottomTabsHeight",
      "setMainThemeID",
    ]),
    setPopupEnabled(isEnabled) {
      this.theMap.layers.forEach((layer) => {
        if (layer.hasOwnProperty("popupEnabled")) {
          layer.popupEnabled = isEnabled;
        }
      });
    },
    mergeSavedMapProfileWithOfficialProfile(profileFromSavedMap) {
      let profileData = profileFromSavedMap.hasOwnProperty("graphics")
        ? {}
        : JSON.parse(JSON.stringify(this.profiles[profileFromSavedMap.id]));

      profileData.id = profileFromSavedMap.id;
      profileData.visible = profileFromSavedMap.visible;
      profileData.opacity = profileFromSavedMap.opacity;
      profileData.open = profileFromSavedMap.open;

      if (profileFromSavedMap.hasOwnProperty("date")) {
        profileData.date = profileFromSavedMap.date;
      }
      if (profileFromSavedMap.hasOwnProperty("graphics")) {
        profileData.graphics = profileFromSavedMap.graphics;
        profileData.appTitle = profileFromSavedMap.appTitle;
        profileData.Temaforklaring = profileFromSavedMap.Temaforklaring;
      } else {
        profileData.ShowAtStartUpLayers =
          profileFromSavedMap.showAtStartupLayers;
      }

      return profileData;
    },
    getDefaultHighlightSymbol(view, geometryType) {
      let h = view.highlightOptions;

      switch (geometryType) {
        case "point":
          return {
            type: "web-style",
            name: "Pushpin 3",
            portal: { url: "https://www.arcgis.com" },
            styleName: "EsriIconsStyle",
          };

        case "polyline":
          return {
            type: "simple-line",
            style: "solid",
            width: 2,
            color: [h.color.r, h.color.g, h.color.b, 1],
          };

        case "polygon":
          return {
            type: "simple-fill",
            style: "solid",
            outline: {
              color: [h.color.r, h.color.g, h.color.b, h.color.a],
              width: 1,
            },
            color: [h.color.r, h.color.g, h.color.b, h.fillOpacity],
          };

        default:
          return {
            type: "simple-line",
            style: "solid",
            width: 2,
            color: [h.color.r, h.color.g, h.color.b, 1],
          };
      }
    },
    getDefaultSymbolBasedOnGeometryType(geometry) {
      switch (geometry.type) {
        case "point":
          return {
            type: "web-style",
            name: "Pushpin 3",
            portal: { url: "https://www.arcgis.com" },
            styleName: "EsriIconsStyle",
          };

        case "polyline":
          return {
            type: "simple-line",
            style: "solid",
            width: 2,
            color: [32, 150, 243, 1],
          };

        case "polygon":
          return {
            type: "simple-fill",
            style: "solid",
            outline: { color: [0, 0, 0, 0.95] },
            color: [32, 150, 243],
          };

        default:
      }
    },
    pasteGeometryIntoGraphicsLayer(payload) {
      loadModules(["esri/Graphic"]).then(([Graphic]) => {
        let graphicsLayer = Vue.prototype.$theMapObject.findLayerById(
          payload.layerID
        );
        let symbol = this.getDefaultSymbolBasedOnGeometryType(payload.geometry);
        let attributes = {
          name: "",
        };
        graphicsLayer.graphics.add(
          new Graphic({
            geometry: payload.geometry,
            symbol: symbol,
            attributes: attributes,
          })
        );
      });
    },
    enablePopupGeometryHighlight(view) {
      view.popup.watch("selectedFeature", (gra) => {
        if (gra) {
          let graCopy = gra.clone();

          removeAllGraphicsExceptLocationGraphic(view);

          if (graCopy.geometry && graCopy.geometry.type !== "point") {
            graCopy.symbol = this.getDefaultHighlightSymbol(
              view,
              graCopy.geometry.type
            );
            view.graphics.add(graCopy);
          }
        }
      });

      view.popup.watch("visible", (vis) => {
        if (!vis) {
          removeAllGraphicsExceptLocationGraphic(view);
        }
      });
    },
    // Saving
    setMapExtent(extent) {
      this.saveExtent(extent);
      Vue.prototype.$theMapViewObject.extent = extent;
      if (typeof Vue.prototype.$theSceneViewObject !== "undefined") {
        Vue.prototype.$theSceneViewObject.extent = extent;
      }
    },
    setMapOrientation(orientation) {
      this.setCurrentOrientation(orientation);
      Vue.prototype.$theMapViewObject.rotation = orientation;
    },
    updateMapFromSavedSettings(savedMapDefinition) {
      if (savedMapDefinition.hasOwnProperty("extent")) {
        this.setMapExtent(savedMapDefinition.extent);
      }

      if (savedMapDefinition.hasOwnProperty("orientation")) {
        this.setMapOrientation(savedMapDefinition.orientation);
      }

      if (
        savedMapDefinition.hasOwnProperty("is3d") &&
        savedMapDefinition.is3d &&
        savedMapDefinition.hasOwnProperty("camera")
      ) {
        switchMapTo3d(savedMapDefinition.camera);
      } else {
        switchMapTo2d();
      }

      this.removeAllMapProfiles(true);

      if (savedMapDefinition.hasOwnProperty("basemap")) {
        this.setBasemap(
          this.basemaps.find((aBasemap) => {
            return aBasemap.id === savedMapDefinition.basemap.id;
          })
        );
      }
    },
    addSavedMapToLastVisitedMaps(savedMap) {
      this.addLastVisitedMap({
        id: savedMap.attributes.GlobalID,
        hash: null,
        title: savedMap.attributes.tittel,
      });
    },
    addProfilesFromSavedMap(savedMapDefinition) {
      const addProfilePromise = (profile) =>
        this.addSingleProfileFromSavedMap(profile).then(() => {});

      // Fjerner potensielle duplikater
      let uniqueAddedProfiles = savedMapDefinition.addedProfiles.filter(
        (v, i, a) => a.findIndex((t) => t.id === v.id) === i
      );

      // Sjekker om fotolag er lagt til
      let hasFoto = uniqueAddedProfiles.findIndex(function(addedProfile) {
        return addedProfile.id === "foto";
      });

      // Aktivér foto hvis lagt til
      if (hasFoto > -1) {
        uniqueAddedProfiles.splice(hasFoto, 1);
        this.setPhotoStreamIsActivated(true);
      }
      // Sequentially resolve promises
      uniqueAddedProfiles
        .reduce(
          (previousPromise, nextID) =>
            previousPromise.then((results) =>
              addProfilePromise(nextID).then((result) => {
                this.setLayersAreLoading(false);
                return results.concat(result);
              })
            ),
          Promise.resolve([])
        )
        .then(() => {
          Object.keys(savedMapDefinition.layerDefinitionExpressions).forEach(
            (item) => {
              this.updateLayerDefinitionExpression(
                savedMapDefinition.layerDefinitionExpressions[item]
              );
            }
          );
        });
    },
    openSavedMap(savedMapAndSettings) {
      let savedMapDefinition = getSavedMapDefinition(savedMapAndSettings);

      this.updateMapFromSavedSettings(savedMapDefinition);

      this.setCurrentlyOpenedProject(savedMapAndSettings.map);

      this.setPhotoStreamIsActivated(false);

      this.addSavedMapToLastVisitedMaps(savedMapAndSettings.map);

      this.addProfilesFromSavedMap(savedMapDefinition);

      savedMapAndSettings.allowSave ? this.enableSave() : this.disableSave();
    },
    addSingleProfileFromSavedMap(profileFromSavedMap) {
      return new Promise((resolve, reject) => {
        let profileData = this.mergeSavedMapProfileWithOfficialProfile(
          profileFromSavedMap
        );

        if (profileData.id === "bygninger3d") {
          this.setBuildingsLayerAdded(true);
        }

        this.addMapProfile(profileData)
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    saveMap(projectMetadata) {
      return new Promise((resolve, reject) => {
        let saveableFeature = this.getMapAsSaveableFeature(projectMetadata);

        SaveService.saveMap(saveableFeature, this.currentlyOpenedProject)
          .then((result) => {
            if (this.currentlyOpenedProject == null) {
              // Sett GlobalID og OBJECTID hvis nytt kart
              saveableFeature.attributes.GlobalID =
                result.addFeatureResults[0].globalId;
              saveableFeature.attributes.OBJECTID =
                result.addFeatureResults[0].objectId;

              if (!projectMetadata.attributes.midlertidig) {
                this.setCurrentlyOpenedProject(saveableFeature);
              }

              this.addLastVisitedMap({
                id: saveableFeature.attributes.GlobalID.toLowerCase(),
                hash: saveableFeature.attributes.GlobalID.toLowerCase().hashCode(),
                title: saveableFeature.attributes.tittel,
              });
              this.enableSave();
            }
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getMapAsSaveableFeature(projectMetadata) {
      let mapObjectJSON = this.getCurrentMapAsJSON();

      let geometry = Vue.prototype.$theMapViewObject.center;

      return {
        geometry: geometry,
        attributes: {
          JSON: mapObjectJSON,
          eier:
            this.userAccount !== null
              ? this.userAccount.accountIdentifier
              : null,
          tittel: projectMetadata.attributes.tittel,
          Offentlig: projectMetadata.attributes.Offentlig,
          redigering: projectMetadata.attributes.redigering,
          midlertidig: projectMetadata.attributes.midlertidig,
        },
      };
    },
    getCurrentMapAsJSON() {
      let addedMapProfiles = this.getCurrentlyAddedMapProfiles();

      let mapObject = {
        extent: this.extent,
        is3d: this.is3d,
        orientation: this.currentOrientation,
        addedProfiles: addedMapProfiles,
        basemap: this.selectedBasemap,
        layerDefinitionExpressions: this.layerDefinitionExpressions,
      };

      if (this.is3d) {
        mapObject.camera = Vue.prototype.$theSceneViewObject.camera.clone();
      } else {
        mapObject.viewpoint = Vue.prototype.$theMapViewObject.viewpoint.clone();
      }

      return JSON.stringify(mapObject);
    },
    getCurrentlyAddedMapProfiles() {
      let addedMapProfiles = [];
      this.mapProfiles.forEach((mapProfile) => {
        let profileDefinition = {
          id: mapProfile.id,
          visible: mapProfile.visible,
          opacity: mapProfile.opacity,
          open: mapProfile.open,
        };
        if (mapProfile.type === "graphicsLayer") {
          profileDefinition.graphics = getLayerGraphicsAsJSON(mapProfile.id);
          profileDefinition.appTitle = mapProfile.appTitle;
          profileDefinition.Temaforklaring = mapProfile.Temaforklaring;
        }
        if (mapProfile.type === "imageryLayer") {
          profileDefinition.date = mapProfile.date;
        }

        if (mapProfile.type === "mapImageLayer") {
          profileDefinition.showAtStartupLayers = this.getMapProfilesVisibleSublayerIndexes(
            mapProfile.id
          );
        }
        addedMapProfiles.push(profileDefinition);
      });
      return addedMapProfiles;
    },
    getProjectByID(projectID) {
      return new Promise((resolve, reject) => {
        loadModules(["esri/tasks/QueryTask", "esri/tasks/support/Query"])
          .then(function([QueryTask, Query]) {
            let queryTask = new QueryTask({
              url: process.env.VUE_APP_MAP_SAVE_SERVICE_URL,
              requestOptions: {
                cacheBust: true,
              },
            });
            let query = new Query();
            query.returnGeometry = false;
            query.outFields = ["*"];
            query.where = "GlobalID = '" + projectID + "'";

            queryTask.execute(query).then(function(results) {
              resolve(results.features[0]);
            });
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    zoomToQuery(url, query) {
      // f.eks. http://localhost:8080/tema/kvikkleire?layer=0&field=skredOmrID&value=123&buffer=1000
      getFirstValidExtentFromURLQuery({
        url: url,
        layer: query.layer,
        field: query.field,
        value: query.value,
      }).then((extent) => {
        let buffer = parseInt(query.buffer);
        if (buffer) {
          extent.xmin = extent.xmin - buffer;
          extent.ymin = extent.ymin - buffer;
          extent.xmax = extent.xmax + buffer;
          extent.ymax = extent.ymax + buffer;
        }
        Vue.prototype.$theMapViewObject.extent = extent;
      });
    },
    getAddedProfile(addedProfileID, addedProfilesWithDisabledVisibility) {
      let addedProfileMetadata = this.getMapProfile(addedProfileID);
      let visible = true;
      let open = true;
      if (addedProfilesWithDisabledVisibility) {
        if (addedProfilesWithDisabledVisibility.includes(addedProfileID)) {
          visible = false;
          open = false;
        }
      }
      return {
        id: addedProfileID,
        showInfoMenu: false,
        visible: visible,
        type: addedProfileMetadata.type,
        open: open,
        canBeDownloaded: addedProfileMetadata.canBeDownloaded,
      };
    },
    addAddedProfilesIfPresent(profile) {
      return new Promise((resolve, reject) => {
        if (profile.AddedProfiles) {
          let promises = [];
          profile.AddedProfiles.forEach((addedProfileID) => {
            let addedProfile = this.getAddedProfile(
              addedProfileID,
              profile.DisableVisibilityOfAddedProfiles
            );
            promises.push(this.addMapProfile(addedProfile, false));
          });
          Promise.all(promises.reverse())
            .then((results) => {
              results.forEach((result) => {
                addLayerToMap(result.layer);
                this.addMapProfileToState(result.profile);
                this.setLayersAreLoading(false);
              });
              resolve(true);
            })
            .catch((error) => {
              reject(error);
            });
        } else {
          resolve(true);
        }
      });
    },
    setCustomLogoAndColorIfDefined(profile) {
      if (profile.hasOwnProperty("logo")) {
        this.setLogoImagePath(profile.logo);
      }
      if (profile.hasOwnProperty("startupDialog")) {
        const dialogAlreadyShownSessionStorageName =
          profile.id + "-startup-dialog-shown";
        if (!sessionStorage.getItem(dialogAlreadyShownSessionStorageName)) {
          profile.startupDialog.active = true;
          this.setStartupDialog(profile.startupDialog);
          sessionStorage.setItem(dialogAlreadyShownSessionStorageName, true);
        }
      }
      if (profile.hasOwnProperty("color")) {
        Vue.prototype.$vuetify.theme.primary = profile.color;
      }
    },
    zoomToPoint(zoomToOptions) {
      loadModules(["esri/geometry/Point", "esri/Graphic"]).then(function([
        Point,
        Graphic,
      ]) {
        let point = new Point({
          x: zoomToOptions.x,
          y: zoomToOptions.y,
          spatialReference: {
            wkid: zoomToOptions.wkid,
          },
        });

        Vue.prototype.$theMapViewObject.goTo({
          target: point,
          zoom: zoomToOptions.zoom,
        });

        const pointGraphic = new Graphic({
          geometry: point,
        });

        // Add the graphics to the view's graphics layer
        Vue.prototype.$theMapViewObject.graphics.addMany([pointGraphic]);
      });
    },
    addMapProfilesAndAddedProfiles(payload) {
      this.setLayersAreLoading(true);
      payload.id = payload.id.toLowerCase();
      let profile = this.getMapProfile(payload.id);

      importProfileFile(payload.id)
        .then((file) => {
          this.setCustomLogoAndColorIfDefined(file.profile);
          this.setMainThemeID(file.profile.id);
          document.title = file.profile.hovedTemaTitle
            ? file.profile.hovedTemaTitle
            : file.profile.appTitle;

          if (payload.zoomToQuery) {
            this.zoomToQuery(
              payload.zoomToQuery.useCountiesAndMunicipalities
                ? process.env.VUE_APP_COUNTIES_AND_MUNICIPALITIES_SERVICE
                : file.profile.serviceUrl + "/",
              payload.zoomToQuery
            );
          }
          if (payload.zoomToPoint) {
            this.zoomToPoint(payload.zoomToPoint);
          }
          if (payload.customAddedProfiles) {
            file.profile.AddedProfiles = payload.customAddedProfiles;
          }

          this.addAddedProfilesIfPresent(file.profile).then(() => {
            this.addMapProfile({
              id: payload.id,
              showInfoMenu: true,
              type: profile.type,
              canBeDownloaded: profile.canBeDownloaded,
            }).then(() => {
              this.setLayersAreLoading(false);
            });
          });
        })
        .catch((error) => {
          console.error(error);
          this.setLayersAreLoading(false);
          if (payload.zoomToPoint) {
            this.zoomToPoint(payload.zoomToPoint);
          }
        });
    },
    async duplicateGraphicsLayerAndAddToMap(graphicsLayerProfile) {
      let existingGraphicsLayer = getLayerByID(graphicsLayerProfile.id);

      const duplicateGraphicsLayerID = getUniqueLayerID();

      let duplicatedGraphicsLayer = await createGraphicsLayer({
        id: duplicateGraphicsLayerID,
      });

      existingGraphicsLayer.graphics.items.forEach((graphic) => {
        duplicatedGraphicsLayer.graphics.add(graphic.clone());
      });

      addLayerToMap(duplicatedGraphicsLayer);

      let duplicateGraphicsLayerProfile = getDuplicateLayerProfile(
        duplicateGraphicsLayerID,
        graphicsLayerProfile.appTitle,
        graphicsLayerProfile.Temaforklaring
      );

      this.addMapProfileToState(duplicateGraphicsLayerProfile);
    },
    loadMetadata(layer) {
      return new Promise((resolve, reject) => {
        MetadataService.loadMetadata(layer)
          .then((newLayerMetadata) => {
            if (newLayerMetadata === false) {
              resolve(false);
            }
            this.addLayerMetadata(newLayerMetadata);
            resolve(true);
          })
          .catch((error) => {
            this.setSnack("Metadata kunne ikke lastes.");
            reject(error);
          });
      });
    },
    addProxyRule(profile) {
      loadModules(["esri/core/urlUtils"]).then(([urlUtils]) => {
        let proxyUrl = "";
        if (
          profile.hasOwnProperty("OnlyAccessibleViaIntranet") &&
          profile.OnlyAccessibleViaIntranet
        ) {
          proxyUrl = process.env.VUE_APP_INTERNAL_PROXY_URL;
        } else {
          proxyUrl = process.env.VUE_APP_PROXY_URL;
        }
        urlUtils.addProxyRule({
          urlPrefix: profile.serviceUrl,
          proxyUrl: proxyUrl,
        });
      });
    },
    addMapProfile(options, addToMap = true) {
      return new Promise((resolve, reject) => {
        if (this.isGraphicsProfile(options)) {
          this.createAndAddGraphicsLayer(options).then((graphicsLayer) => {
            resolve(graphicsLayer);
          });
        } else {
          importProfileFile(options.id)
            .then((profileFile) => {
              let profile = mergeProfileOptions(
                cloneObject(profileFile.profile),
                options
              );
              if (options.visible || typeof options.visible === "undefined") {
                this.setCustomLogoAndColorIfDefined(profile);
              }

              this.addProxyRuleIfProfileIsInternal(profile);

              createMapLayer(
                {
                  is3d: this.is3d,
                  isConnectedToIntranet: this.isConnectedToIntranet,
                  type: options.type,
                },
                profile
              ).then((layer) => {
                if (options.type === "mapImageLayer") {
                  this.setProfileActualVisibleSublayers({
                    id: profile.id,
                    VisibleLayers: profile.VisibleLayers,
                  });
                }
                if (addToMap) {
                  addLayerToMap(layer);
                  placePhotoLayerOnTopIfActive();

                  this.addMapProfileToState(profile);

                  if (options.showInfoMenu) {
                    this.setSelectedTheme(profile.id);
                  }
                  resolve(layer);
                } else {
                  resolve({ profile: profile, layer: layer });
                }
              });
            })
            .catch((error) => {
              this.setSnack("Temaet kunne ikke hentes fra serveren.");
              console.error(error);
              reject(error);
            });
        }
      });
    },
    isGraphicsProfile(profile) {
      return (
        !this.profiles.hasOwnProperty(profile.id) ||
        profile.hasOwnProperty("graphics")
      );
    },
    addProxyRuleIfProfileIsInternal(profile) {
      if (
        (profile.hasOwnProperty("OnlyAccessibleViaIntranet") &&
          profile.OnlyAccessibleViaIntranet) ||
        (profile.hasOwnProperty("useProxy") && profile.useProxy)
      ) {
        this.addProxyRule(profile);
      }
    },
    async createAndAddGraphicsLayer(options) {
      return new Promise(async (resolve, reject) => {
        options.open = options.open ? options.open : true;
        options.opacity = options.opacity ? options.opacity : 1.0;
        options.visible = options.visible ? options.visible : true;

        let existingGraphicsLayer = getLayerByID(options.id);

        if (!existingGraphicsLayer) {
          if (options.hasOwnProperty("graphics")) {
            try {
              options.graphics = await getHydratedGraphics(options.graphics);
            } catch (error) {
              reject(error);
            }
          }

          try {
            existingGraphicsLayer = await createGraphicsLayer(options);
          } catch (error) {
            reject(error);
          }

          addLayerToMap(existingGraphicsLayer);
        }

        this.addMapProfileToState(getGraphicslayerMapProfile(options));

        resolve(existingGraphicsLayer);
      });
    },
    updateMapProfileDate(updateInfo) {
      let exisitingLayer = this.theMap.findLayerById(updateInfo.id);
      exisitingLayer.mosaicRule.where =
        "opptakstidspunkt >= '" +
        updateInfo.newDate +
        " 00:00:00' AND opptakstidspunkt <= '" +
        updateInfo.newDate +
        " 23:59:59'";
      exisitingLayer.refresh();
      let index = this.mapProfiles.findIndex(
        (profile) => profile.id === updateInfo.id
      );
      this.setMapProfileDate({
        index: index,
        newDate: updateInfo.newDate,
      });
    },
    updateLayerDefinitionExpression(payload) {
      let topLayer = Vue.prototype.$theMapObject.findLayerById(
        payload.parentLayerID
      );
      let sublayer = topLayer.allSublayers.items.find((item) => {
        return item.id == payload.sublayerIndex;
      });

      sublayer.definitionExpression = payload.sqlString;
      this.setLayerDefinitionExpression(payload);
    },
    removeMapProfile(id) {
      let exisitingLayer = this.theMap.findLayerById(id);
      this.theMap.remove(exisitingLayer);
      this.removeMapProfileFromState(id);

      if (this.selectedTheme === id) {
        this.setShowInfoMenu(false);
      }
    },
    removeAllMapProfiles(removeBuildings) {
      let mapProfilesToRemove = [];
      this.mapProfiles.forEach((profile) => {
        if (
          (profile.id !== "bygninger3d" || removeBuildings) &&
          profile.id !== "foto"
        ) {
          mapProfilesToRemove.push(profile.id);
        }
      });
      mapProfilesToRemove.forEach((id) => {
        this.removeMapProfile(id);
      });
    },
    saveExtent(extent) {
      if (Vue.prototype.$theMapViewObject) {
        Vue.$cookies.set("extent", JSON.stringify(extent));
        this.setExtent(extent);
        this.setCurrentMapScale(
          this.is3d && Vue.prototype.$theSceneViewObject
            ? Vue.prototype.$theSceneViewObject.scale
            : Vue.prototype.$theMapViewObject.scale
        );
      }
    },
    setMapLayerVisibility(payload) {
      let layer = this.theMap.findLayerById(payload.profile.id);

      if (layer) {
        layer.visible = payload.visible;
      }

      if (payload.visible) {
        this.setCustomLogoAndColorIfDefined(payload.profile);
      }

      this.setProfileVisibility(payload);
    },
    setMapLayerOpacity(payload) {
      let layer = Vue.prototype.$theMapObject.findLayerById(payload.id);
      layer.opacity = payload.opacity;
      let mapProfileIndex = this.getAddedMapProfileIndex(payload.id);
      this.setMapProfileOpacity({
        mapProfileIndex: mapProfileIndex,
        opacity: payload.opacity,
      });
    },
    loadLayerInfo(layer) {
      return loadModules(["esri/core/urlUtils"]).then(([urlUtils]) => {
        const proxyRule = urlUtils.getProxyRule(layer.url);
        let getUrl = layer.url;
        if (proxyRule) {
          getUrl = proxyRule.proxyUrl + getUrl;
        }

        return Vue.axios.get(getUrl + "?f=json").then((response) => {
          let info = response.data;
          info.url = layer.url;
          this.addLayerInfo(info);
        });
      });
    },
    zoomTo(extent) {
      Vue.prototype.$theMapViewObject.goTo(extent);
    },
  },
};

export default mappingMixin;
